import React, {useCallback, useEffect, useState} from 'react';
import {Box, Button, CircularProgress, Slider, Stack, Typography} from '@mui/material';
import PropTypes from 'prop-types';
import {UploadFile} from '@mui/icons-material';
import {downloadFormDocument, useUploadFormDocumentsMutation} from '../../../../../api/services/filesService';
import AdmicityDialog from '../../../../../shared-components/AdmicityDialog';
import Uploader from '../../../../SchoolManagement/Setup/Uploader';
import RichEditor from './Shared/RichEditor';
import RichEditorResultText from './Shared/RichEditorResultText';

const Image = ({
                   componentId,
                   index,
                   onChange,
                   descriptionHtml,
                   onDescriptionHtmlChange,
                   commitDescriptionHtml,
                   isEditMode,
                   attachments,
                   imageWidthPercent,
                   isFormArchived,
                   readOnly,
                   placeholderValues,
                   accessLevel,
                   audience,
                   recipientType,
                   linkedToStudents,
                   linkedToEmployees,
                   submitMode
               }) => {
    const [uploadFiles] = useUploadFormDocumentsMutation();
    const [openAddDocumentsDialog, setOpenAddDocumentsDialog] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [isUploadSizeExceedsLimit, setIsUploadSizeExceedsLimit] = useState(false);
    const [previewImageUrl, setPreviewImageUrl] = useState(undefined);

    const [draftDescriptionHtml, setDraftDescriptionHtml] = useState(descriptionHtml || '');
    const [isEditorFocused, setIsEditorFocused] = useState(false);

    useEffect(() => {
        if (isEditorFocused) return;
        setDraftDescriptionHtml(descriptionHtml || '');
    }, [descriptionHtml, isEditorFocused]);

    const handleDraftChange = useCallback((nextHtml) => {
        setDraftDescriptionHtml(nextHtml);
        onDescriptionHtmlChange(nextHtml);
    }, [onDescriptionHtmlChange]);

    const handleFocus = useCallback(() => {
        setIsEditorFocused(true);
    }, []);

    const handleBlur = useCallback(() => {
        setIsEditorFocused(false);
        commitDescriptionHtml();
    }, [commitDescriptionHtml]);

    useEffect(() => {
        if (attachments && attachments.length) {
            downloadFormDocument({documentId: attachments[0].id}).then(r => {
                setPreviewImageUrl(URL.createObjectURL(r.blob));
            });
        }

        return () => {
            if (previewImageUrl) {
                URL.revokeObjectURL(previewImageUrl);
            }
        };
    }, [attachments]);

    const handleFilesUpload = async () => {
        setIsUploading(true);
        const uploadedFiles = await uploadFiles(selectedFiles);
        onChange({index, files: [uploadedFiles.data[0]]});
        handleDialogClose();
    };

    // AD-2689 (Maria, 14 October 2026): the picture's size as a percentage of the width of the form. Until a size
    // is chosen the picture keeps its own size, no wider than the form.
    const [sizeDraft, setSizeDraft] = useState(imageWidthPercent ?? 100);
    useEffect(() => {
        setSizeDraft(imageWidthPercent ?? 100);
    }, [imageWidthPercent]);
    const imageSx = imageWidthPercent
        ? {width: `${imageWidthPercent}%`, height: 'auto'}
        : {maxWidth: '100%', height: 'auto'};

    const handleDialogClose = () => {
        setOpenAddDocumentsDialog(false);
        setSelectedFiles([]);
        setIsUploading(false);
        setIsUploadSizeExceedsLimit(false);
    };

    const handleFilesSelect = (files) => {
        setSelectedFiles(files);
    };

    const handleFileDeleteFromUploadContext = (filename) => {
        const files = [...selectedFiles];
        setSelectedFiles(files.filter(f => f.name !== filename));
    };

    return (
        <Box sx={{mt: 2}}>
            {isEditMode
                ? (
                    <>
                        <Button
                            component="label"
                            variant="outlined"
                            startIcon={<UploadFile/>}
                            onClick={() => setOpenAddDocumentsDialog(true)}
                            disabled={isFormArchived}
                        >
                            Choose Image
                        </Button>

                        {previewImageUrl &&
                            <>
                                <Stack direction="row" alignItems="center" spacing={2} sx={{mt: 2, maxWidth: 420}}>
                                    <Typography variant="body2" sx={{whiteSpace: 'nowrap'}}>Image size</Typography>
                                    <Slider
                                        size="small"
                                        min={10}
                                        max={100}
                                        step={5}
                                        value={sizeDraft}
                                        disabled={isFormArchived}
                                        valueLabelDisplay="auto"
                                        valueLabelFormat={(value) => `${value}%`}
                                        onChange={(_, value) => setSizeDraft(value)}
                                        onChangeCommitted={(_, value) => onChange({index, imageWidthPercent: value})}
                                        onMouseDown={(event) => event.stopPropagation()}
                                        data-testid={`image-size-${componentId}`}
                                        aria-label="Image size"
                                    />
                                    <Typography variant="body2" sx={{minWidth: 40}}>{sizeDraft}%</Typography>
                                </Stack>
                                <Box py={1} maxWidth="100%" display="flex" justifyContent="center">
                                    <Box component="img" src={previewImageUrl} sx={imageWidthPercent
                                        ? {width: `${sizeDraft}%`, height: 'auto'}
                                        : {maxWidth: '100%', height: 'auto'}}/>
                                </Box>
                            </>
                        }

                        <RichEditor
                            onChange={onChange}
                            isEditMode={isEditMode}
                            isFormLive={isFormArchived}
                            accessLevel={accessLevel}
                            currentDescription={draftDescriptionHtml}
                            setCurrentDescription={handleDraftChange}
                            audience={audience}
                            recipientType={recipientType}
                            linkedToStudents={linkedToStudents}
                            linkedToEmployees={linkedToEmployees}
                            placeholderValues={placeholderValues}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                        />

                        <AdmicityDialog
                            handleClose={handleDialogClose}
                            title="Add documents"
                            open={openAddDocumentsDialog}
                            actions={[
                                {
                                    label: 'Cancel',
                                    onClick: handleDialogClose
                                },
                                {
                                    label: 'Upload',
                                    onClick: handleFilesUpload,
                                    disabled: (isUploading || selectedFiles.size === 0) || isUploadSizeExceedsLimit,
                                    startIcon: isUploading ? <CircularProgress color="inherit" size={16}/> : undefined
                                }
                            ]}
                            maxWidth="md"
                        >
                            <Uploader
                                totalUploadSizeInMb={5}
                                onFilesSelect={handleFilesSelect}
                                onFileDelete={handleFileDeleteFromUploadContext}
                                onUploadLimitExceed={setIsUploadSizeExceedsLimit}
                                value={selectedFiles}
                                readonly={isUploading}
                                singleFile
                                fileTypes={{
                                    'image/jpeg': [],
                                    'image/png': [],
                                }}
                                allowedFileTypesLabel="Allowed file types: JPEG, PNG"
                            />
                        </AdmicityDialog>
                    </>
                )
                : (
                    <>
                        <RichEditorResultText
                            id={componentId}
                            description={descriptionHtml}
                            placeholderValues={placeholderValues}
                            readOnly={readOnly}
                            enableValidation={false}
                            audience={audience}
                            recipientType={recipientType}
                            linkedToStudents={linkedToStudents}
                            linkedToEmployees={linkedToEmployees}
                            submitMode={submitMode}
                        />
                        {previewImageUrl &&
                            <Box py={1} key={`file-${previewImageUrl}`} maxWidth="100%">
                                <Box component="img" src={previewImageUrl} sx={imageSx}/>
                            </Box>
                        }
                    </>
                )
            }
        </Box>
    );
};

Image.propTypes = {
    componentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    index: PropTypes.number,
    onChange: PropTypes.func,
    descriptionHtml: PropTypes.string,
    onDescriptionHtmlChange: PropTypes.func.isRequired,
    commitDescriptionHtml: PropTypes.func.isRequired,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    attachments: PropTypes.array,
    imageWidthPercent: PropTypes.number,
    readOnly: PropTypes.bool,
    placeholderValues: PropTypes.object,
    accessLevel: PropTypes.number,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    linkedToStudents: PropTypes.bool,
    linkedToEmployees: PropTypes.bool,
    submitMode: PropTypes.bool
};

export default Image;
